// The Orders tab's filters and page, carried in the URL (#1512).
//
// They used to live only in the tab's component state, so opening an order and
// coming back reset the list. In the URL they survive the round trip, and the
// order detail page can read them — which is what lets its Previous / Next
// step through the FILTERED list rather than the whole one.
//
// Plain functions so a test can call them without rendering either page
// (`.claude/rules/code/logic-outside-the-host.md`).

import {
  ORDER_COLUMN_FILTER_KEYS, ORDER_LIST_SORTS, ORDER_LIST_DEFAULT_SORT,
  type OrderColumnFilters, type OrderListSort,
} from '@/app-kit/use-orders';
import { SORT_DIRS, type SortDir, type SortState } from '@/app-kit/table-sort';

/** Search-param name for the list's 1-based page. */
export const ORDER_LIST_PAGE_PARAM = 'page';
/**
 * Search-param names for the list's click-to-sort. In the URL for the same
 * reason as the filters: the detail page's Previous / Next walk the list in
 * the order it was sorted.
 */
export const ORDER_LIST_SORT_PARAM = 'sort';
export const ORDER_LIST_DIR_PARAM  = 'dir';

/** The list's sort from the URL; anything unrecognised is the default. */
export function orderSortFromSearch(sp: URLSearchParams): SortState<OrderListSort> {
  const key = sp.get(ORDER_LIST_SORT_PARAM) ?? '';
  const dir = sp.get(ORDER_LIST_DIR_PARAM) ?? '';
  if (!(ORDER_LIST_SORTS as readonly string[]).includes(key)) return ORDER_LIST_DEFAULT_SORT;
  return {
    key: key as OrderListSort,
    dir: (SORT_DIRS as readonly string[]).includes(dir) ? (dir as SortDir) : 'asc',
  };
}

/** The list's filters as the URL carries them — absent keys read as blank. */
export function orderFiltersFromSearch(sp: URLSearchParams): OrderColumnFilters {
  const filters: OrderColumnFilters = {};
  for (const k of ORDER_COLUMN_FILTER_KEYS) filters[k] = sp.get(k) ?? '';
  return filters;
}

/** The list's page from the URL; anything but a positive integer is page 1. */
export function orderPageFromSearch(sp: URLSearchParams): number {
  const n = Number(sp.get(ORDER_LIST_PAGE_PARAM));
  return Number.isInteger(n) && n > 1 ? n : 1;
}

/**
 * The search string (`?…`, or `''`) for a filter set, page and sort. Blank
 * filters, page 1 and the default sort are left out, so an unfiltered first
 * page has a bare URL.
 */
export function orderListSearch(
  filters: OrderColumnFilters, page = 1, sort: SortState<OrderListSort> = ORDER_LIST_DEFAULT_SORT,
): string {
  const sp = new URLSearchParams();
  for (const k of ORDER_COLUMN_FILTER_KEYS) {
    const v = (filters[k] ?? '').trim();
    if (v.length > 0) sp.set(k, v);
  }
  if (page > 1) sp.set(ORDER_LIST_PAGE_PARAM, String(page));
  // The default order is left out, like page 1, so a plain list has a bare URL.
  if (sort.key !== null
      && !(sort.key === ORDER_LIST_DEFAULT_SORT.key && sort.dir === ORDER_LIST_DEFAULT_SORT.dir)) {
    sp.set(ORDER_LIST_SORT_PARAM, sort.key);
    sp.set(ORDER_LIST_DIR_PARAM, sort.dir);
  }
  const s = sp.toString();
  return s ? `?${s}` : '';
}
